/**
 * DiscoveryChat Component - Conversational onboarding
 *
 * Version History:
 * v4.31 - 2026-10-16 - DISCOVERY-CONFIRM-CHAIN S-10 / S-11 (row 551, findings #10 #11): the review screen
 *   marks every field the server reports as a suggested default (summaryOrigin from skip, start(skip) and
 *   resume); an unchanged save no longer marks a field edited; Continue commits an edit still open in the
 *   text box; edited fields are kept in sessionStorage by session id (restored on resume, cleared after a
 *   successful confirm).
 * v3.14 - 2026-09-13 - Discovery error handling: never advance onboarding when nothing
 *   was saved. Failures no longer swallow silently or fabricate a session.
 *   - startDiscovery failure: show mapped error + retry/support (no fabricated greeting)
 *   - startSkipDiscovery / confirmSkipDiscovery failure: show error + retry/support (no onComplete)
 *   - handleConfirmAndContinue: confirm failure falls back to the skip path (industry
 *     defaults); if skip also fails, show error + retry/support. onComplete() only after
 *     something was actually saved.
 * v2.54 - 2026-02-03 - True skip: Skip mode applies defaults immediately, goes straight to review
 * v2.50 - 2026-02-03 - Fixed skip button in mode selection (filter skip from toggle group)
 * v2.48 - 2026-02-03 - Added Skip Discovery option:
 *   - Skip from mode selection (only asks company_name + role)
 *   - Skip button during active chat
 *   - Default challenges applied when skipping
 * v2.25 - 2026-01-30 - Major UX improvements:
 *   - Added Quick (5min) vs Full (15min) discovery mode toggle
 *   - Added timer display showing estimated remaining time
 *   - Multi-select suggestion chips with Send button
 *   - Auto-focus input after AI responds
 *   - Added role question
 *   - Review screen before transition with Edit/Continue options
 *   - Save transcript to local drive + account
 *   - Better hurry-up detection integration
 * v2.24 - 2026-01-30 - Fixed: Call confirm() endpoint when discovery reaches review status
 * v2.6 - 2026-01-25 - Handle 'review' status as completion
 * v2.5 - 2026-01-23 - BFF pattern: removed tenantId prop
 * v2.4 - 2026-01-23 - Fixed API call signatures
 * v2.2 - 2026-01-23 - Initial implementation
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  LinearProgress,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  List,
  ListItem,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TimerIcon from '@mui/icons-material/Timer';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { discoveryService } from '../services/api';
// v3.14 - Reuse the portal's canonical support widget (MKTG-03) for onboarding
// error recovery instead of inventing a new destination.
import ContactFormModal from './ContactFormModal';
// v1.0 - CHAT-ERR-SAFE-LIST-01: only surface backend reasons on the shared safe list - 2026-09-13
import { resolveDiscoveryError } from '../utils/chatErrorSafeList';
// v4.18 - DISCOVERY-LIFECYCLE F02b - 2026-09-15: pure resume helpers (no axios, no React).
import { resumeStartTime, elapsedSecondsSince } from './discoveryResume';

interface Message {
  id: string;
  type: 'ai' | 'user';
  content: string;
  timestamp: Date;
}

interface DiscoverySummary {
  [key: string]: string;
}

interface DiscoveryChatProps {
  onComplete: () => void;
}

// v2.25 - Discovery mode configuration
// v2.48 - Added skip mode option - 2026-02-03
const DISCOVERY_MODES = {
  quick: { label: 'Quick', duration: 5, description: '~5 minutes - Key questions only' },
  full: { label: 'Full', duration: 15, description: '~15 minutes - Comprehensive discovery' },
  skip: { label: 'Skip', duration: 1, description: 'Skip discovery - use defaults' },
};

// v2.48 - Skip discovery message
const SKIP_DISCOVERY_MESSAGE = "Common business challenges will be applied automatically. MyLojic will learn more about your specific needs through ongoing conversations.";

const DiscoveryChat: React.FC<DiscoveryChatProps> = ({ onComplete }) => {
  // Core state
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentObjective, setCurrentObjective] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  // v2.25 - New state for enhanced UX
  // v2.48 - Added skip mode support
  const [discoveryMode, setDiscoveryMode] = useState<'quick' | 'full' | 'skip'>('quick');
  const [showModeSelection, setShowModeSelection] = useState(true);
  // v4.18 - DISCOVERY-LIFECYCLE F02c - 2026-09-15: the resume check has not resolved yet.
  // Initial state is the mode chooser, so before this flag the chooser painted first and
  // was replaced a round-trip later - contrary to the item's own plan text (plan-v1 C12).
  const [resumeChecking, setResumeChecking] = useState(true);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [discoverySummary, setDiscoverySummary] = useState<DiscoverySummary>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false); // v2.48 - Skip confirmation dialog

  // v3.14 - Error recovery state. When a save-bearing step fails, we surface a
  // customer-safe message and offer retry + support instead of advancing the wizard
  // with nothing saved.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryAction, setRetryAction] =
    useState<'start' | 'startSkip' | 'confirmSkip' | 'confirm' | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false); // explicit-skip notice

  // v4.16 - DISCOVERY-LIFECYCLE F05 - 2026-09-15: the keys the customer actually edited.
  // handleConfirmAndContinue used to post the WHOLE discoverySummary as the edit map, so
  // the server stamped edited:true on every answer including the ones nobody touched --
  // destroying the only signal that distinguishes a customer's own words from an industry
  // default. A ref, not state: it must not trigger a re-render and is read at submit time.
  const dirtyKeysRef = useRef<Set<string>>(new Set());

  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-10: the server's per-field origin ("default" | "basic_info" |
  // "customer"); empty when the response carries none (a normal chat review), so no marker is shown.
  const [summaryOrigin, setSummaryOrigin] = useState<Record<string, string>>({});

  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-11: the customer's edited fields survive a remount in the same tab.
  // Only this tab's sessionStorage holds them; storage failures (private mode, blocked storage) are ignored.
  const persistEdits = (sid: string, summary: DiscoverySummary) => {
    try {
      const out: Record<string, string> = {};
      dirtyKeysRef.current.forEach((k) => {
        if (typeof summary[k] === 'string') {
          out[k] = summary[k];
        }
      });
      sessionStorage.setItem(`discoveryEdits:${sid}`, JSON.stringify(out));
    } catch {
      /* storage unavailable: edits stay in memory only */
    }
  };

  // v4.16 - F05 + F09: post only edited keys. An untouched review posts {}.
  // v4.31 - S-11: takes the summary to post, so an edit committed on Continue is included.
  const buildConfirmEdits = (summary: DiscoverySummary = discoverySummary): Record<string, string> => {
    const edits: Record<string, string> = {};
    dirtyKeysRef.current.forEach((k) => {
      const v = summary[k as keyof typeof summary];
      if (typeof v === 'string') {
        edits[k] = v;
      }
    });
    return edits;
  };

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // v2.25 - Timer effect
  useEffect(() => {
    if (startTime && !showReview) {
      const interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [startTime, showReview]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // v2.25 - Auto-focus input after AI responds
  useEffect(() => {
    if (!isLoading && inputRef.current && !showReview && !showModeSelection) {
      inputRef.current.focus();
    }
  }, [isLoading, showReview, showModeSelection]);

  // v4.16 - DISCOVERY-LIFECYCLE F02 - 2026-09-15: resume an open session on mount.
  //
  // The session id lived only in component state, so a refresh mid-discovery lost it
  // irrecoverably; the only remaining action was to start again, which created a second
  // row and orphaned the first with no way to reach, resume or reset it. This asks the
  // server -- which is where the session actually lives -- before offering a mode choice.
  //
  // Runs once. A 404 (nothing open) resolves to null and leaves the mode chooser exactly
  // as it was; any other error is left to the normal start path to surface rather than
  // blocking the wizard on a resume that is an optimisation, not a prerequisite.
  const resumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const open = await discoveryService.open();
        if (cancelled || !open) return;

        setSessionId(open.session_id);
        setDiscoveryMode((open.mode as 'quick' | 'full' | 'skip') || 'quick');
        setProgress(open.progress || 0);
        setShowModeSelection(false);
        // v4.18 - DISCOVERY-LIFECYCLE F02b - 2026-09-15: continue the server's clock instead
        // of restarting it. elapsedSeconds is set once here as well, because the timer effect
        // ticks only while startTime is set AND the review screen is closed - a session
        // resumed straight into review would otherwise render a zero duration.
        const resumedStart = resumeStartTime(open, new Date());
        setStartTime(resumedStart);
        setElapsedSeconds(elapsedSecondsSince(resumedStart, new Date()));

        if (open.status === 'review') {
          // v4.31 - S-11: re-apply this tab's stored edits over the server summary and mark them dirty.
          const resumedSummary: DiscoverySummary = { ...((open.summary as DiscoverySummary) || {}) };
          const restoredOrigin: Record<string, string> = {};
          try {
            const raw = sessionStorage.getItem(`discoveryEdits:${open.session_id}`);
            const stored = raw ? JSON.parse(raw) : null;
            Object.entries(stored || {}).forEach(([k, v]) => {
              if (typeof v === 'string' && k in resumedSummary) {
                resumedSummary[k] = v;
                dirtyKeysRef.current.add(k);
                restoredOrigin[k] = 'customer';
              }
            });
          } catch {
            /* unreadable storage: resume with the server summary */
          }
          setDiscoverySummary(resumedSummary);
          // v4.31 - S-10
          setSummaryOrigin(open.summaryOrigin ? { ...open.summaryOrigin, ...restoredOrigin } : restoredOrigin);
          setShowReview(true);
        } else {
          setCurrentObjective(open.current_objective || '');
          setSuggestions(open.suggestions || []);
          if (open.question) {
            addMessage('ai', open.question);
          }
        }
      } catch (resumeError) {
        // Non-fatal by design: the customer can still start. Do not strand the wizard.
        console.warn('Discovery resume check failed; offering a fresh start:', resumeError);
      } finally {
        // v4.18 - F02c: every exit path clears the spinner - resumed, 404, thrown error, and
        // cancelled-after-resolution. No path leaves resumeChecking true.
        setResumeChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v2.25 - Calculate remaining time
  const getRemainingTime = useCallback(() => {
    const targetMinutes = DISCOVERY_MODES[discoveryMode].duration;
    const targetSeconds = targetMinutes * 60;
    const remaining = Math.max(0, targetSeconds - elapsedSeconds);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [discoveryMode, elapsedSeconds]);

  // v2.25 - Format elapsed time for display
  const getElapsedDisplay = useCallback(() => {
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [elapsedSeconds]);

  // v3.14 - Map any thrown error to a customer-safe message. The axios response
  // interceptor (services/api.ts) attaches `userMessage` via getErrorMessage; we never
  // surface raw backend detail. Non-axios throws (e.g. "No session ID") fall back to a
  // generic message.
  const getUserFacingMessage = (error: unknown): string =>
    (error as { userMessage?: string })?.userMessage ||
    'Something went wrong and your progress was not saved. Please try again.';

  // Start discovery with selected mode
  const startDiscovery = async () => {
    setShowModeSelection(false);
    setStartTime(new Date());
    setIsLoading(true);
    setErrorMessage(null);

    try {
      // v2.25 - Pass discovery mode to backend
      const response = await discoveryService.start({ mode: discoveryMode });
      setSessionId(response.session_id);
      // v4.18 - DISCOVERY-LIFECYCLE F02b - 2026-09-15: discovery_start returns the SAME
      // resume payload for an existing open session, so honour its clock here too. A fresh
      // start keeps the new Date() set above.
      if (response.resumed === true) {
        const resumedStart = resumeStartTime(response, new Date());
        setStartTime(resumedStart);
        setElapsedSeconds(elapsedSecondsSince(resumedStart, new Date()));
      }
      setCurrentObjective(response.current_objective || '');
      setSuggestions(response.suggestions || []);
      setProgress(response.progress || 0);
      if (response.question) {
        addMessage('ai', response.question);
      }
    } catch (error) {
      // v3.14 - Do NOT fabricate a greeting + role chips. The old fallback created a
      // chat with no session_id, so every subsequent answer/confirm failed silently and
      // the customer could be advanced with nothing saved. Show the error, offer recovery.
      console.error('Failed to start discovery:', error);
      setErrorMessage(getUserFacingMessage(error));
      setRetryAction('start');
    } finally {
      setIsLoading(false);
    }
  };

  // v2.54 - Start skip discovery - applies defaults immediately and goes to review
  // v3.13 - Fix #2: Set isLoading BEFORE clearing showModeSelection to prevent chat UI flash
  const startSkipDiscovery = async () => {
    setShowSkipConfirm(false);
    setDiscoveryMode('skip');
    setStartTime(new Date());
    setIsLoading(true);
    setShowModeSelection(false);
    setErrorMessage(null);

    try {
      // v2.54 - Pass 'skip' mode to backend - returns review status immediately
      const response = await discoveryService.start({ mode: 'skip' });
      setSessionId(response.session_id);
      setProgress(response.progress || 100);
      // v4.16 - F01: the notice belongs HERE, where defaults genuinely were applied. It
      // was previously set only by the confirm fallback, where it was false — nothing had
      // been saved — and never on the explicit skip, where it is true.
      setDefaultsApplied(true);

      // v2.54 - Skip mode returns status: 'review' with summary, go straight to review
      if (response.status === 'review' || response.skipped) {
        setDiscoverySummary(response.summary || {});
        setSummaryOrigin(response.summaryOrigin || {});  // v4.31 - S-10
        setShowReview(true);
      } else {
        // Fallback: if backend didn't return review, show a message
        setCurrentObjective(response.current_objective || '');
        setSuggestions(response.suggestions || []);
        if (response.question) {
          addMessage('ai', response.question);
        }
      }
    } catch (error) {
      // v3.14 - Never advance with nothing saved. The old fallback called onComplete()
      // here, pushing the customer to Connect Data with no discovery record. Show the
      // mapped error and offer retry + support instead.
      console.error('Failed to start skip discovery:', error);
      setErrorMessage(getUserFacingMessage(error));
      setRetryAction('startSkip');
    } finally {
      setIsLoading(false);
    }
  };

  // v2.48 - Handle skip during active discovery
  const handleSkipDuringDiscovery = () => {
    setShowSkipConfirm(true);
  };

  // v2.48 - Confirm skip and save with defaults
  const confirmSkipDiscovery = async () => {
    setShowSkipConfirm(false);
    setIsLoading(true);
    setErrorMessage(null);

    try {
      if (sessionId) {
        // Call backend to skip and save defaults
        const response = await discoveryService.skip(sessionId);
        setDefaultsApplied(true);  // v4.16 - F01: true on the explicit skip, unlike the
                                   // removed confirm fallback where it was not.

        if (response.status === 'complete' || response.status === 'review') {
          setProgress(100);
          setDiscoverySummary(response.summary || {});
          setSummaryOrigin(response.summaryOrigin || {});  // v4.31 - S-10
          addMessage('ai', "Got it! I'll use common business challenges for now. We can learn more through our conversations.");
          setTimeout(() => {
            setShowReview(true);
          }, 1000);
        }
      }
    } catch (error) {
      // v3.14 - Never advance with nothing saved. The old fallback called onComplete()
      // here. Show the mapped error and offer retry + support instead; the customer
      // stays on the discovery step.
      console.error('Failed to skip discovery:', error);
      setErrorMessage(getUserFacingMessage(error));
      setRetryAction('confirmSkip');
    } finally {
      setIsLoading(false);
    }
  };

  const addMessage = (type: 'ai' | 'user', content: string) => {
    const newMessage: Message = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  // v2.25 - Handle multi-select chip toggle
  const handleSuggestionToggle = (suggestion: string) => {
    setSelectedSuggestions((prev) =>
      prev.includes(suggestion)
        ? prev.filter((s) => s !== suggestion)
        : [...prev, suggestion]
    );
  };

  // v2.25 - Send selected suggestions
  const handleSendSuggestions = () => {
    if (selectedSuggestions.length > 0) {
      const message = selectedSuggestions.join(', ');
      handleSendMessage(message);
      setSelectedSuggestions([]);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    addMessage('user', message);
    setInputValue('');
    setSelectedSuggestions([]);
    setIsLoading(true);
    setSuggestions([]);

    try {
      if (!sessionId) {
        throw new Error('No session ID');
      }

      const response = await discoveryService.answer(sessionId, currentObjective, message);

      // v2.25 - Check for review status and show review screen
      if (response.status === 'complete' || response.status === 'review') {
        setProgress(100);
        setDiscoverySummary(response.summary || {});
        setSummaryOrigin({});  // v4.31 - S-10: the answer path's review carries no origin; no markers

        // Show review screen instead of auto-transitioning
        addMessage('ai', "Great! I've got a good understanding of your business. Let me summarize what I've learned.");

        setTimeout(() => {
          setShowReview(true);
        }, 1000);
      } else {
        setCurrentObjective(response.current_objective);
        setSuggestions(response.suggestions || []);
        setProgress(response.progress || 0);
        addMessage('ai', response.question);
      }
    } catch (error: any) {
      console.error('Failed to submit answer:', error);
      // v1.0 - CHAT-ERR-SAFE-LIST-01: show the backend reason (re-keyed to
      // data.error by the BFF) only when it is on the safe list; otherwise keep
      // the existing fixed line. - 2026-09-13
      addMessage('ai', resolveDiscoveryError(error, "I didn't quite catch that. Could you tell me more?"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  // v2.25 - Handle edit in review screen
  const handleStartEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    if (editingField) {
      const key = editingField;
      // v4.31 - DISCOVERY-CONFIRM-CHAIN S-11: an unchanged save is not an edit. Marking it dirty posted the
      // default back as the customer's own value and relabelled it "customer".
      if (editValue !== discoverySummary[key]) {
        // v4.16 - F05: record the key as dirty so confirm posts this one and no other.
        dirtyKeysRef.current.add(key);
        const next: DiscoverySummary = { ...discoverySummary, [key]: editValue };
        setDiscoverySummary(next);
        setSummaryOrigin((prev) => ({ ...prev, [key]: 'customer' }));
        if (sessionId) {
          persistEdits(sessionId, next);
        }
      }
      setEditingField(null);
      setEditValue('');
    }
  };

  // v2.25 - Confirm and continue to Connect Data
  // v3.14 - Never advance the wizard unless something was actually saved.
  // v4.16 - DISCOVERY-LIFECYCLE F01 - 2026-09-15: the skip fallback is REMOVED.
  //
  // It converted a hard backend refusal into a success screen. The confirm endpoint
  // returns 503 stage1_lineage_incomplete when a required Stage 1 record cannot be
  // written, and deliberately persists NOTHING in that case so the customer can retry.
  // The old catch block answered that 503 by calling /skip -- which by its own contract
  // writes the SESSION document only, never tenant_meta and never workflowState -- then
  // showed "we applied common industry defaults" and advanced after 1500ms. The customer
  // was told their setup had progressed when nothing had been saved, and a refresh sent
  // them back to the previous step anyway.
  //
  // New behaviour:
  //   1. Confirm succeeds -> advance.
  //   2. Confirm fails (any status, session or not) -> show the mapped error, stay on the
  //      review screen, offer retry + support. Do NOT call skip. Do NOT advance.
  // Skip remains reachable only as an explicit customer action (confirmSkipDiscovery).
  const handleConfirmAndContinue = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      if (!sessionId) {
        // No session means nothing to save. Do not advance with nothing saved.
        throw new Error('No session ID');
      }
      // v4.31 - DISCOVERY-CONFIRM-CHAIN S-11: an edit still open in the text box is committed first, so
      // pressing Continue without Save does not lose it.
      let summaryForConfirm: DiscoverySummary = discoverySummary;
      if (editingField) {
        if (editValue !== discoverySummary[editingField]) {
          dirtyKeysRef.current.add(editingField);
          summaryForConfirm = { ...discoverySummary, [editingField]: editValue };
          setDiscoverySummary(summaryForConfirm);
        }
        setEditingField(null);
        setEditValue('');
      }
      await discoveryService.confirm(sessionId, buildConfirmEdits(summaryForConfirm));
      console.log('Discovery confirmed, tenant_meta created');
      // v4.31 - S-11: the saved edits are now the server's; drop this tab's copy.
      try {
        sessionStorage.removeItem(`discoveryEdits:${sessionId}`);
      } catch {
        /* storage unavailable: nothing to clear */
      }
      setIsLoading(false);
      onComplete();
      return;
    } catch (confirmError) {
      console.error('Failed to confirm discovery:', confirmError);
      setErrorMessage(getUserFacingMessage(confirmError));
      setRetryAction('confirm');
      setIsLoading(false);
    }
  };

  // v3.14 - Retry the failed step from the error screen, re-invoking the CURRENT handler
  // (fresh closure over state) rather than a stale captured function.
  const handleRetry = () => {
    const action = retryAction;
    setErrorMessage(null);
    setRetryAction(null);
    switch (action) {
      case 'start':
        startDiscovery();
        break;
      case 'startSkip':
        startSkipDiscovery();
        break;
      case 'confirmSkip':
        confirmSkipDiscovery();
        break;
      case 'confirm':
        handleConfirmAndContinue();
        break;
      default:
        break;
    }
  };

  // v2.25 - Download transcript to local drive
  const handleDownloadTranscript = () => {
    const transcript = messages
      .map((m) => `[${m.type.toUpperCase()}] ${m.content}`)
      .join('\n\n');

    const summaryText = Object.entries(discoverySummary)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    const fullText = `DISCOVERY TRANSCRIPT\n${'='.repeat(50)}\nDate: ${new Date().toLocaleString()}\nDuration: ${getElapsedDisplay()}\n\n${'='.repeat(50)}\nCONVERSATION\n${'='.repeat(50)}\n\n${transcript}\n\n${'='.repeat(50)}\nSUMMARY\n${'='.repeat(50)}\n\n${summaryText}`;

    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discovery-transcript-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // v2.25 - Save to account (cloud storage)
  const handleSaveToAccount = async () => {
    setIsSaving(true);
    try {
      // This would call an API to save to tenant's blob storage
      // For now, just simulate success
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setShowSaveDialog(false);
      // Show success message (could add a snackbar here)
    } catch (error) {
      console.error('Failed to save transcript:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Format field names for display
  const formatFieldName = (key: string) => {
    const labels: Record<string, string> = {
      role: 'Your Role',
      industry: 'Industry',
      company_size: 'Company Size',
      challenges: 'Challenges',
      data_sources: 'Data Sources',
      pain_points: 'Pain Points',
      decision_makers: 'Decision Makers',
      success_metrics: 'Success Metrics',
      yearly_objectives: 'Yearly Goals',
      other_concerns: 'Other Concerns',
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // v3.14 - Error recovery screen. Shown whenever a save-bearing step failed. The
  // customer stays here (the wizard has NOT advanced) and can retry the failed step or
  // reach support via the portal's canonical contact widget (ContactFormModal, MKTG-03).
  if (errorMessage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          We couldn't save your progress
        </Typography>
        <Alert severity="error">{errorMessage}</Alert>
        <Typography variant="body2" color="text.secondary">
          Your onboarding has not moved on, so nothing is lost. Please try again. If this
          keeps happening, contact support and we'll help you get set up.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={isLoading}
          >
            {isLoading ? 'Retrying...' : 'Try Again'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowContactModal(true)}
            disabled={isLoading}
          >
            Contact Support
          </Button>
        </Box>
        <ContactFormModal
          open={showContactModal}
          onClose={() => setShowContactModal(false)}
          title="Contact Support"
          initialMessage="I ran into a problem finishing the discovery step during onboarding and my progress wasn't saved."
        />
      </Box>
    );
  }

  // v4.18 - DISCOVERY-LIFECYCLE F02c - 2026-09-15: hold the frame until the resume check
  // resolves. CircularProgress is already imported and already rendered by this component in
  // two other branches; no new component and no new import.
  if (resumeChecking) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" color="text.secondary">
          Checking for a session in progress...
        </Typography>
      </Box>
    );
  }

  // v2.25 - Mode selection screen
  // v2.48 - Added skip discovery option
  if (showModeSelection) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Let's Learn About Your Business
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose how detailed you'd like the discovery conversation to be:
        </Typography>

        {/* v2.50 - Only show quick and full modes in toggle group, skip is separate */}
        <ToggleButtonGroup
          value={discoveryMode}
          exclusive
          onChange={(_, value) => value && setDiscoveryMode(value)}
          sx={{ display: 'flex', gap: 2 }}
        >
          {Object.entries(DISCOVERY_MODES)
            .filter(([mode]) => mode !== 'skip')
            .map(([mode, config]) => (
            <ToggleButton
              key={mode}
              value={mode}
              sx={{
                flex: 1,
                flexDirection: 'column',
                py: 3,
                px: 4,
                border: '2px solid',
                borderColor: discoveryMode === mode ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.main' },
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {config.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {config.description}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Button
          variant="contained"
          size="large"
          onClick={startDiscovery}
          sx={{ mt: 2, py: 1.5 }}
        >
          Start Discovery
        </Button>

        {/* v2.48 - Skip Discovery option */}
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">or</Typography>
        </Divider>

        <Box
          sx={{
            p: 2,
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: 2,
            bgcolor: 'grey.50',
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.100' },
          }}
          onClick={() => setShowSkipConfirm(true)}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Skip Discovery
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {SKIP_DISCOVERY_MESSAGE}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            We'll just ask for your company name and role.
          </Typography>
        </Box>

        {/* v2.48 - Skip confirmation dialog */}
        <Dialog open={showSkipConfirm} onClose={() => setShowSkipConfirm(false)}>
          <DialogTitle>Skip Discovery?</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to skip the discovery process?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {SKIP_DISCOVERY_MESSAGE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              We'll still ask for your <strong>company name</strong> and <strong>role</strong> to personalize your experience.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSkipConfirm(false)}>
              Go Back
            </Button>
            <Button variant="contained" onClick={startSkipDiscovery}>
              Yes, Skip Discovery
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // v2.25 - Review screen before transition
  if (showReview) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Discovery Complete</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Duration: {getElapsedDisplay()}
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Please review the summary below. You can edit any field before continuing.
        </Typography>

        {/* v3.14 - Shown when confirm failed and we saved industry defaults via the skip
            fallback instead. The customer is about to be advanced automatically. */}
        {defaultsApplied && (
          <Alert severity="info" sx={{ mb: 2 }}>
            We applied common industry defaults so you can keep going. You can refine these
            anytime in Settings.
          </Alert>
        )}

        {/* Summary Card */}
        <Card sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
          <CardContent>
            <List dense>
              {Object.entries(discoverySummary).map(([key, value]) => (
                <ListItem
                  key={key}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    py: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                        {formatFieldName(key)}
                      </Typography>
                      {/* v4.31 - DISCOVERY-CONFIRM-CHAIN S-10: per-field marker for a suggested default */}
                      {summaryOrigin[key] === 'default' && (
                        <Chip size="small" variant="outlined" label="Suggested default" />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleStartEdit(key, value)}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {editingField === key ? (
                    <Box sx={{ width: '100%', display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      />
                      <Button size="small" variant="contained" onClick={handleSaveEdit}>
                        Save
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {value || '(not provided)'}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTranscript}
          >
            Download
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => setShowSaveDialog(true)}
          >
            Save to Account
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleConfirmAndContinue}
            disabled={isLoading}
            sx={{ px: 4 }}
          >
            {isLoading ? 'Saving...' : 'Continue to Connect Data'}
          </Button>
        </Box>

        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)}>
          <DialogTitle>Save Transcript</DialogTitle>
          <DialogContent>
            <Typography>
              Save this discovery transcript to your account for future reference?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveToAccount}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // v3.13 - Fix #2: Show spinner when skip mode is loading (prevents chat UI flash)
  if (discoveryMode === 'skip' && isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" color="text.secondary">
          Preparing your summary...
        </Typography>
      </Box>
    );
  }

  // Main chat interface
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '500px' }}>
      {/* v2.25 - Progress Bar with Timer */}
      {/* v2.48 - Added Skip button */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Discovery Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
            {/* v2.25 - Timer display */}
            {discoveryMode !== 'skip' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <TimerIcon fontSize="small" />
                <Typography variant="body2">
                  ~{getRemainingTime()} remaining
                </Typography>
              </Box>
            )}
            {/* v2.48 - Skip button during chat */}
            <Button
              size="small"
              variant="text"
              onClick={handleSkipDuringDiscovery}
              disabled={isLoading}
              sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 1 }}
            >
              Skip
            </Button>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Chat Messages */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          mb: 2,
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            <Box
              sx={{
                maxWidth: '80%',
                p: 2,
                borderRadius: 2,
                bgcolor: message.type === 'user' ? 'primary.main' : 'white',
                color: message.type === 'user' ? 'white' : 'text.primary',
                boxShadow: message.type === 'user' ? 0 : 1,
              }}
            >
              <Typography variant="body1">{message.content}</Typography>
            </Box>
          </Box>
        ))}

        {/* Typing Indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'white',
                boxShadow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Paper>

      {/* v2.25 - Multi-Select Suggestion Chips */}
      {suggestions.length > 0 && !isLoading && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {suggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                onClick={() => handleSuggestionToggle(suggestion)}
                color={selectedSuggestions.includes(suggestion) ? 'primary' : 'default'}
                variant={selectedSuggestions.includes(suggestion) ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: selectedSuggestions.includes(suggestion)
                      ? 'primary.dark'
                      : 'grey.200',
                  },
                }}
              />
            ))}
          </Box>
          {/* v2.25 - Send button for selected chips */}
          {selectedSuggestions.length > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSendSuggestions}
              startIcon={<SendIcon />}
              sx={{ mt: 1 }}
            >
              Send Selected ({selectedSuggestions.length})
            </Button>
          )}
        </Box>
      )}

      {/* Input Field */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          placeholder="Type your answer or select suggestions above..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          variant="outlined"
          size="small"
          inputRef={inputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
        />
        <IconButton
          onClick={() => handleSendMessage(inputValue)}
          disabled={isLoading || !inputValue.trim()}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.300' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* v2.48 - Skip confirmation dialog during chat */}
      <Dialog open={showSkipConfirm} onClose={() => setShowSkipConfirm(false)}>
        <DialogTitle>Skip Remaining Questions?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to skip the rest of the discovery?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {SKIP_DISCOVERY_MESSAGE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSkipConfirm(false)}>
            Continue Discovery
          </Button>
          <Button variant="contained" onClick={confirmSkipDiscovery}>
            Yes, Skip
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscoveryChat;
